import React, { useRef, useState } from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { trackMetaEvent } from '../utils/metaPixel';
interface FormState {
  nombre: string;
  telefono: string;
  correo: string;
  modalidad: string;
}
const initialState: FormState = {
  nombre: '',
  telefono: '',
  correo: '',
  modalidad: 'Presencial'
};
const fieldClass = 'w-full border-0 border-b border-ink/20 bg-transparent px-0 py-4 font-sans text-base text-ink placeholder:text-steel-400/70 transition-colors duration-200 ease-out focus:border-champagne focus:outline-none focus:ring-0';
const labelClass = 'block font-sans text-[11px] font-medium uppercase tracking-[0.18em] text-steel';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xdeopzbz';
export function LeadForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadTracked = useRef(false);
  const update = (key: keyof FormState, value: string) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: new FormData(formEl),
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        if (!leadTracked.current) {
          leadTracked.current = true;
          trackMetaEvent('Lead');
        }
        formEl.reset();
        setForm(initialState);
        setSubmitted(true);
      } else {
        const details = await response.text();
        console.error('Formspree error response:', response.status, details);
        setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
      }
    } catch (err) {
      console.error('Formspree request failed:', err);
      setError('No pudimos enviar tu información. Por favor, inténtalo nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };
  return <section id="formulario" className="w-full scroll-mt-16 bg-ivory-dark py-24 sm:py-32 lg:py-40" aria-labelledby="form-title" data-id="element-151" data-mp="2hw7c7kswll4o">
      <div className="mx-auto max-w-3xl px-5 sm:px-10 lg:px-12" data-id="element-152" data-mp="2cxw0t5myn2kc">
        {submitted ? <div className="py-10 text-center sm:py-16" data-id="element-153" data-mp="285m5tb6zncb7">
            <span className="mx-auto flex h-14 w-14 items-center justify-center border border-champagne" data-id="element-154" data-mp="237axx6097bvm">
              <CheckIcon className="h-6 w-6 text-champagne" strokeWidth={1.5} data-id="element-155" data-mp="1y8huk6eol196" />
            </span>
            <h2 className="mt-10 font-sans text-3xl font-light uppercase leading-tight tracking-[0.02em] text-ink sm:text-[2.75rem]" data-id="element-156" data-mp="1u6d0j6kbm2m0">
              ¡Gracias!
            </h2>
            <p className="mx-auto mt-6 max-w-xl font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-157" data-mp="1p9w29jjeq7v7">
              Recibimos tus datos. Una persona de nuestro equipo se pondrá en
              contacto contigo para coordinar la conversación y responder tus
              preguntas.
            </p>
          </div> : <>
            <Reveal data-id="element-158" data-mp="inz2x6px4vb5">
              <h2 id="form-title" className="font-sans text-[2rem] font-light uppercase leading-[1.1] text-ink sm:text-5xl xl:text-[3.4rem]" data-id="element-159" data-mp="ioh4mivco1qg">
                Coordinemos una
                <br data-id="element-160" data-mp="3m3mptzuf6a7h" />
                conversación.
              </h2>
              <p className="mt-7 max-w-xl font-sans text-[17px] font-light leading-relaxed text-steel sm:text-lg" data-id="element-161" data-mp="3r3r4rlfoiwvl">
                Déjanos tus datos y una persona de nuestro equipo te contactará
                para coordinar.
              </p>
            </Reveal>

            <form action={FORMSPREE_ENDPOINT} method="POST" onSubmit={handleSubmit} className="mt-14 space-y-12 border-t border-ink/15 pt-12 sm:mt-16" data-id="element-162" data-mp="33epqvio2utix">
              <div className="space-y-9" data-id="element-163" data-mp="14z8su6kzboxa">
                <div data-id="element-164" data-mp="1ip2ius880f8l">
                  <label htmlFor="nombre" className={labelClass} data-id="element-165" data-mp="1iplx9yynwlhv">
                    Nombre y apellido *
                  </label>
                  <input id="nombre" name="nombre" type="text" required autoComplete="name" value={form.nombre} onChange={e => update('nombre', e.target.value)} className={fieldClass} data-id="element-166" data-mp="1dwsw1l2ejvaj" />
                </div>
                <div data-id="element-167" data-mp="1xnebmnsh33uh">
                  <label htmlFor="telefono" className={labelClass} data-id="element-168" data-mp="1xn4ln07fgwos">
                    Teléfono *
                  </label>
                  <input id="telefono" name="telefono" type="tel" required autoComplete="tel" value={form.telefono} onChange={e => update('telefono', e.target.value)} className={fieldClass} data-id="element-169" data-mp="7bqiwsfl0grp" />
                </div>
                <div data-id="element-170" data-mp="3fi5bfhi3frb8">
                  <label htmlFor="correo" className={labelClass} data-id="element-171" data-mp="3kit2u4a8ia5z">
                    Correo electrónico *
                  </label>
                  <input id="correo" name="email" type="email" required autoComplete="email" value={form.correo} onChange={e => update('correo', e.target.value)} className={fieldClass} data-id="element-172" data-mp="35l0nbc4otlrs" />
                </div>
              </div>

              <div data-id="element-173" data-mp="uua8xuelipb0">
                <p className={labelClass} data-id="element-174" data-mp="uro1durpg9ss">Modalidad de la conversación</p>
                <input type="hidden" name="modalidad" value={form.modalidad} data-id="element-175" data-mp="zrivs8aovhil" />
                <p className="mt-5 font-sans text-lg font-medium uppercase tracking-[0.16em] text-ink" data-id="element-176" data-mp="xef8qvw9mo97">
                  Presencial
                </p>
                <p className="mt-3 font-sans text-base font-light text-steel" data-id="element-177" data-mp="tey3o3kr3t4k">
                  Miami, FL 33172
                </p>
                <p className="mt-4 max-w-lg font-sans text-sm font-light leading-relaxed text-steel-400" data-id="element-178" data-mp="jexfo4bxsou1">
                  Si no te encuentras cerca de esta zona, podemos coordinar un
                  punto de encuentro que funcione para ambas partes.
                </p>
              </div>

              <div data-id="element-179" data-mp="17bteo1kkbun9">
                <button type="submit" disabled={submitting} className="group inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-5 font-sans text-xs font-medium uppercase tracking-[0.2em] text-ivory transition-colors duration-200 ease-out hover:bg-ink-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto" data-id="element-180" data-mp="17bba8hnqya49">
                  {submitting ? 'Enviando...' : 'Agendar una conversación'}
                  {!submitting && <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-181" data-mp="2c9ook8bf1g1o" />}
                </button>

                {error && <p role="alert" className="mt-5 max-w-lg font-sans text-sm font-light leading-relaxed text-ink" data-id="element-182" data-mp="2q9zhri0776q0">
                    {error}
                  </p>}
              </div>
            </form>
          </>}
      </div>
    </section>;
}